/**
 * Mail API — Send email notifications via Microsoft Graph Mail API.
 *
 * All Inbox emails are sent from Marinus's work Microsoft account.
 * Used for new file notifications, SLA warnings, and weekly reports.
 */

import { graphRequest } from './graphClient';
import { formatDateZA, formatDateTimeZA } from '../utils/dateHelpers';

/**
 * Vicky's NOTIFICATION email address (New Business Assistant).
 *
 * Mirrors how REACT_APP_WOUTER_EMAIL is read for Wouter's notifications.
 * NOTE: this is deliberately NOT her login address — she logs in with
 * bridgesvix@gmail.com (see VICKY_LOGIN_EMAIL in src/auth/authConfig.js) but
 * receives email at her work address, set via REACT_APP_VICKY_EMAIL in the
 * Cloudflare Pages settings.
 *
 * The prep-request notifications themselves (notify Vicky on submit, notify
 * the ORIGINATOR on completion — CLAUDE.md §26.5 / §28.7) are still a
 * DEFERRED feature. This constant exists now so that deferred work has a
 * ready hook; callers must handle the empty-string case (env var unset) by
 * skipping the send, exactly as sendNewFileNotification does for Wouter.
 *
 * ORIGINATOR NOTE (§26.1): a preparation request is loaded by the Adviser
 * OR the Administration Assistant, so any "who asked for this" recipient or
 * wording MUST be resolved from `preparation_requests.created_by` — never
 * hardcoded to the Adviser's address. The completion notification goes to
 * the originator, and the submit notification to Vicky should name the
 * originator as the requester.
 *
 * GUARD NOTE for the deferred prep-request notifications: when they are
 * built, they MUST skip test records (is_test = true, CLAUDE.md §33) —
 * mirror the `if (record?.isTest === true) return;` guard used by
 * sendNewFileNotification / sendSLAWarning below.
 *
 * FUTURE INTEGRATION POINT — task amendments (§26.7/§26.8): when the Adviser
 * or the originating Administration Assistant amends a submitted request's
 * task list, this is where a
 * `sendPrepAmendedNotification`-style send would belong, called from
 * `saveAmendment` in PrepRequestDetail.jsx after the save succeeds. NOTHING
 * is sent today, by design — the in-app acknowledgement banner and
 * completion gate are the notification mechanism. Any such send must be
 * non-blocking (an email failure can never undo an amendment), must skip
 * test records per the guard above, and must not fire on the
 * acknowledgement write.
 *
 * FUTURE INTEGRATION POINT — new preparation rounds (§26.10): notifying the
 * New Business Assistant when the Adviser starts a new round would also
 * belong here (called from startNewRound in PrepRequestDetail.jsx after the
 * reopen succeeds), under the same constraints: non-blocking, skips test
 * records, nothing is sent today by design.
 */
export const VICKY_NOTIFICATION_EMAIL = process.env.REACT_APP_VICKY_EMAIL || '';

/**
 * Send an email using the Microsoft Graph Mail API.
 *
 * @param {object} emailData
 * @param {string} emailData.to — recipient email address
 * @param {string} emailData.subject — email subject
 * @param {string} emailData.body — HTML email body
 */
export async function sendEmail({ to, subject, body }) {
  const message = {
    message: {
      subject,
      body: {
        contentType: 'HTML',
        content: body,
      },
      toRecipients: [
        {
          emailAddress: { address: to },
        },
      ],
    },
    saveToSentItems: true,
  };

  await graphRequest('/me/sendMail', {
    method: 'POST',
    body: JSON.stringify(message),
  });
}

/**
 * Send new file notification to Wouter.
 */
export async function sendNewFileNotification(file, inboxCount) {
  // Test records (is_test = true, CLAUDE.md §33) never trigger
  // notifications. Callers also skip the send, but this guard makes the
  // exclusion hold no matter who calls.
  if (file?.isTest === true) return;
  const wouterEmail = process.env.REACT_APP_WOUTER_EMAIL;
  if (!wouterEmail) return;

  const appUrl = window.location.origin;
  const subject = `📥 New file in Inbox: ${file.clientName} — ${file.workCategory}`;
  const body = `
    <div style="font-family: 'DM Sans', Arial, sans-serif; max-width: 600px; margin: 0 auto;">
      <h2 style="color: #0F4C75;">New File Submitted</h2>
      <p>Hi Wouter,</p>
      <p>A new file has been submitted and is waiting for you in Inbox.</p>
      <table style="margin: 16px 0; border-collapse: collapse;">
        <tr><td style="padding: 4px 12px 4px 0; font-weight: 600;">CLIENT:</td><td>${file.clientName}</td></tr>
        <tr><td style="padding: 4px 12px 4px 0; font-weight: 600;">CATEGORY:</td><td>${file.workCategory}</td></tr>
        <tr><td style="padding: 4px 12px 4px 0; font-weight: 600;">PRIORITY:</td><td>${file.priority}</td></tr>
        <tr><td style="padding: 4px 12px 4px 0; font-weight: 600;">SUBMITTED:</td><td>${formatDateTimeZA(file.submittedAt)}</td></tr>
      </table>
      <p>
        <a href="${appUrl}/file/${file.id}" style="display: inline-block; padding: 10px 24px; background: #0F4C75; color: white; text-decoration: none; border-radius: 8px; font-weight: 500;">
          VIEW FILE IN INBOX →
        </a>
      </p>
      <p>You currently have <strong>${inboxCount}</strong> file(s) waiting in your inbox.</p>
      <hr style="border: none; border-top: 1px solid #D0DAE6; margin: 24px 0;" />
      <p style="color: #6B7A8D; font-size: 13px;">Inbox © 2025 — Private & Confidential</p>
    </div>
  `;

  await sendEmail({ to: wouterEmail, subject, body });
}

/**
 * Send SLA warning email to both Marinus and Wouter.
 */
export async function sendSLAWarning(file, activeDays) {
  // Test records never trigger SLA warning emails (CLAUDE.md §33).
  if (file?.isTest === true) return;
  const marinusEmail = process.env.REACT_APP_MARINUS_EMAIL;
  const wouterEmail = process.env.REACT_APP_WOUTER_EMAIL;
  const appUrl = window.location.origin;
  const subject = `⚠️ SLA Alert: ${file.clientName} file is overdue — Inbox`;

  // Tailored messages per recipient per the spec
  const wouterBody = `
    <div style="font-family: 'DM Sans', Arial, sans-serif; max-width: 600px; margin: 0 auto; color: #1A2B3C;">
      <div style="background: #E76F51; padding: 16px 24px; border-radius: 10px 10px 0 0;">
        <h2 style="color: white; margin: 0; font-size: 18px;">⚠️ SLA Alert</h2>
      </div>
      <div style="background: white; padding: 24px; border: 1px solid #D0DAE6; border-top: none; border-radius: 0 0 10px 10px;">
        <p>Hi Wouter,</p>
        <p>The file for <strong>${file.clientName}</strong> has now exceeded the 3-day processing target.</p>
        <p style="background: rgba(231, 111, 81, 0.08); padding: 12px; border-radius: 8px; border-left: 3px solid #E76F51;">
          This file will <strong>NOT</strong> qualify for your monthly bonus until it is sent for client signature.
        </p>
        <p>Please prioritise this file and finalise it as soon as possible.</p>
        <table style="margin: 16px 0; border-collapse: collapse;">
          <tr><td style="padding: 4px 12px 4px 0; font-weight: 600; color: #6B7A8D;">Active days elapsed:</td><td><strong>${activeDays.toFixed(1)} days</strong></td></tr>
          <tr><td style="padding: 4px 12px 4px 0; font-weight: 600; color: #6B7A8D;">File drawn:</td><td>${file.drawnAt ? formatDateZA(file.drawnAt) : 'N/A'}</td></tr>
        </table>
        <p>
          <a href="${appUrl}/file/${file.id}" style="display: inline-block; padding: 10px 24px; background: #E76F51; color: white; text-decoration: none; border-radius: 8px; font-weight: 500;">
            OPEN FILE →
          </a>
        </p>
        <p style="color: #6B7A8D; font-style: italic; margin-top: 16px;">You've got this — let's get it across the line.</p>
        <hr style="border: none; border-top: 1px solid #D0DAE6; margin: 20px 0;" />
        <p style="color: #6B7A8D; font-size: 12px;">Inbox © 2025 — Private & Confidential</p>
      </div>
    </div>
  `;

  const marinusBody = `
    <div style="font-family: 'DM Sans', Arial, sans-serif; max-width: 600px; margin: 0 auto; color: #1A2B3C;">
      <div style="background: #E76F51; padding: 16px 24px; border-radius: 10px 10px 0 0;">
        <h2 style="color: white; margin: 0; font-size: 18px;">⚠️ SLA Alert</h2>
      </div>
      <div style="background: white; padding: 24px; border: 1px solid #D0DAE6; border-top: none; border-radius: 0 0 10px 10px;">
        <p>Hi Marinus,</p>
        <p>The file for <strong>${file.clientName}</strong> has exceeded the 3-day SLA processing target.</p>
        <table style="margin: 16px 0; border-collapse: collapse;">
          <tr><td style="padding: 4px 12px 4px 0; font-weight: 600; color: #6B7A8D;">Active days elapsed:</td><td><strong>${activeDays.toFixed(1)} days</strong></td></tr>
          <tr><td style="padding: 4px 12px 4px 0; font-weight: 600; color: #6B7A8D;">File drawn:</td><td>${file.drawnAt ? formatDateZA(file.drawnAt) : 'N/A'}</td></tr>
          <tr><td style="padding: 4px 12px 4px 0; font-weight: 600; color: #6B7A8D;">Drawn by:</td><td>${file.drawnBy || 'N/A'}</td></tr>
        </table>
        <p>
          <a href="${appUrl}/file/${file.id}" style="display: inline-block; padding: 10px 24px; background: #E76F51; color: white; text-decoration: none; border-radius: 8px; font-weight: 500;">
            OPEN FILE →
          </a>
        </p>
        <hr style="border: none; border-top: 1px solid #D0DAE6; margin: 20px 0;" />
        <p style="color: #6B7A8D; font-size: 12px;">Inbox © 2025 — Private & Confidential</p>
      </div>
    </div>
  `;

  const sends = [];
  if (wouterEmail) sends.push(sendEmail({ to: wouterEmail, subject, body: wouterBody }));
  if (marinusEmail) sends.push(sendEmail({ to: marinusEmail, subject, body: marinusBody }));

  await Promise.all(sends);
}
